// Display units for takeoff: imperial (feet, inches, SF) or metric (metres, millimetres, m²).
//
// A DISPLAY preference, per viewer — everything is stored and priced in feet / SF / CF, and the estimate keeps its
// own units. This module converts at the edge: what the canvas reads out, the dock, the totals, the exports. It
// converts only quantities in units it knows (LF / SF / CF and the length / area words); anything else (EA, a tile
// count, a unit the catalog invented) passes through unchanged, so nothing is ever mislabelled.

export const UNIT_SYSTEMS = Object.freeze(["imperial", "metric"]);
export const UNITS_STORAGE_KEY = "cs.takeoff.units";

export const FT_TO_M = 0.3048;
export const SF_TO_M2 = FT_TO_M * FT_TO_M;      // 0.09290304
export const CF_TO_M3 = FT_TO_M ** 3;           // 0.028316846592

export const isMetric = (units) => units === "metric";

export function readUnits(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { const v = storage?.getItem(UNITS_STORAGE_KEY); return UNIT_SYSTEMS.includes(v) ? v : "imperial"; } catch { return "imperial"; }
}
export function writeUnits(units, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { storage?.setItem(UNITS_STORAGE_KEY, units); } catch { /* not remembered */ }
}
export const toggleUnits = (units) => (isMetric(units) ? "imperial" : "metric");

const group = (n, d) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const finite = (v) => typeof v === "number" ? Number.isFinite(v) : (v != null && v !== "" && Number.isFinite(+v));

// ── lengths ──────────────────────────────────────────────────────────────────
/** Feet to the way a dimension is read in each system: `83' 5"` or `25.43 m` (`254 mm` under a metre). */
export function fmtLength(ft, units = "imperial") {
  if (!finite(ft)) return "—";
  const v = +ft;
  if (isMetric(units)) {
    const m = Math.abs(v) * FT_TO_M, sign = v < 0 ? "-" : "";
    if (m < 1) return `${sign}${Math.round(m * 1000)} mm`;
    return `${sign}${group(m, 2)} m`;
  }
  const sign = v < 0 ? "-" : "";
  const totalIn = Math.round(Math.abs(v) * 12);
  return `${sign}${Math.floor(totalIn / 12)}' ${totalIn % 12}"`;
}

/** A length as a decimal in the system's own unit with its unit mark: `12.5′` or `3.81 m`. */
export function fmtLengthDecimal(ft, units = "imperial", d = 1) {
  if (!finite(ft)) return "—";
  if (isMetric(units)) return `${group(+ft * FT_TO_M, d + 1)} m`;
  return `${group(+ft, d)}′`;
}

// ── areas and quantities ─────────────────────────────────────────────────────
export function fmtArea(sf, units = "imperial", d = 0) {
  if (!finite(sf)) return "—";
  if (isMetric(units)) { const m2 = +sf * SF_TO_M2; return `${group(m2, m2 >= 100 ? 1 : 2)} m²`; }
  return `${group(+sf, d)} SF`;
}

const KIND_OF_UNIT = (u) => {
  const k = String(u ?? "").trim().toLowerCase();
  if (k === "lf" || k === "ft" || k === "linear ft" || k === "linear feet" || k === "feet") return "length";
  if (k === "sf" || k === "sqft" || k === "sq ft" || k === "ft2") return "area";
  if (k === "cf" || k === "cuft" || k === "cu ft" || k === "ft3") return "volume";
  return null;
};

/** One quantity into the display system: { q, u } -> { q, u } (metric: m, m², m³; anything else unchanged). */
export function convertQuantity({ q, u }, units = "imperial") {
  if (!isMetric(units) || !finite(q)) return { q, u };
  const kind = KIND_OF_UNIT(u);
  if (kind === "length") return { q: +q * FT_TO_M, u: "m" };
  if (kind === "area") return { q: +q * SF_TO_M2, u: "m²" };
  if (kind === "volume") return { q: +q * CF_TO_M3, u: "m³" };
  return { q, u };
}

/** "120.5 LF" / "36.73 m" — a quantity as text, converted when metric. Decimals follow the unit. */
export function fmtQty({ q, u }, units = "imperial") {
  if (!finite(q)) return "—";
  const c = convertQuantity({ q, u }, units);
  const metric = isMetric(units) && c.u !== u;
  // Imperial LF / SF read as whole numbers (utils/quantityRounding.js); other imperial units keep one decimal.
  const whole = !metric && ["lf", "sf"].includes(String(c.u).trim().toLowerCase());
  const d = metric ? (c.u === "m³" ? 3 : 2) : whole ? 0 : 1;
  const n = Math.round(+c.q * 10 ** d) / 10 ** d;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: d })} ${c.u}`.trim();
}

/** The unit word a column header should carry, in the display system: "Length (ft)" -> "Length (m)". */
export function unitWord(word, units = "imperial") {
  if (!isMetric(units)) return word;
  return { ft: "m", sf: "m²", cf: "m³", SF: "m²", LF: "m", CF: "m³", FT: "m" }[word] || word;
}

// ── entering a length ────────────────────────────────────────────────────────
/**
 * A typed length into FEET (what everything is stored in), or null when it cannot be read.
 *   imperial: `12.5`, `12'`, `12' 6"`, `12'-6"`, `150"`   (a bare number is feet)
 *   metric:   `3.8`, `3.8 m`, `380 cm`, `3800 mm`         (a bare number is metres)
 */
export function parseLengthToFeet(text, units = "imperial") {
  const s = String(text ?? "").trim().toLowerCase().replace(/,/g, "");
  if (!s) return null;
  if (isMetric(units)) {
    const m = s.match(/^(-?\d+(?:\.\d+)?|-?\.\d+)\s*(mm|cm|m)?$/);
    if (!m) return null;
    const v = parseFloat(m[1]);
    const metres = m[2] === "mm" ? v / 1000 : m[2] === "cm" ? v / 100 : v;
    return metres / FT_TO_M;
  }
  let m = s.match(/^(-?\d+(?:\.\d+)?|-?\.\d+)\s*(?:'|ft|feet)?$/);
  if (m) return parseFloat(m[1]);
  m = s.match(/^(-?\d+(?:\.\d+)?|-?\.\d+)\s*(?:"|in|inch|inches)$/);
  if (m) return parseFloat(m[1]) / 12;
  m = s.match(/^(-?\d+)\s*(?:'|ft)\s*-?\s*(\d+(?:\.\d+)?)\s*(?:"|in)?$/);
  if (m) { const ft = parseInt(m[1], 10); const inch = parseFloat(m[2]); return ft < 0 ? ft - inch / 12 : ft + inch / 12; }
  return null;
}
